import * as React from "react";
import { Column } from "primereact/column";
import classnames from 'classnames';
import { DataTable } from "primereact/datatable";
import { joinReactNodes } from "../../../../utils/toolsReact";
import { aminoAcidFullNames, aminoAcidNames } from "../../../../commons/units";
import RemoveButton from "../../../../commons/RemoveButton";
import { MutationLevelEnum, ICodonMutation, IMutation } from "../../types/mutation";

interface IProps {
  // required:
  level: MutationLevelEnum,
  targetName: string,
  uniprot: string,
  sequence: string,
  mutations: IMutation[],
  codonMutations: ICodonMutation[] | null,
  clearPosition (position: number | number[]): void,
  emptyMsgMultiple: boolean,
  // optional:
  selectedPosition?: number | null,
}

class MutationTable extends React.PureComponent<IProps> {

  static defaultProps = {
    selectedPosition: null,
  }

  noRowsMsg = "No selected " + (this.props.emptyMsgMultiple ? "mutations" : "mutation") + " to show. Use Textual input or Sequence input.";
  tableClassName = "ll-mutation-table";
  removeBtnStyle = { width: '8%', padding: '0' };

  getAminoAcidLabel(aminoAcidLetter: string, args?: {
    fullName?: boolean,
    style?: 'letter-in-bracket' | 'strong-letter'
  }) {
    const fullName = args !== undefined && args.fullName !== undefined ? args.fullName : false;
    const style = args !== undefined && args.style !== undefined ? args.style : null;
    if (aminoAcidLetter !== "X") {
      const name = (fullName === true ? aminoAcidFullNames : aminoAcidNames)[aminoAcidLetter];
      switch (style) {
        case 'strong-letter':
          return <><strong>{aminoAcidLetter}</strong> - {name}</>;
        case 'letter-in-bracket':
        default:
          return `${name} (${aminoAcidLetter})`;
      }
    } else {
      return "invalid amino acid";
    }
  }

  nameBody = () => this.props.targetName;

  uniprotBody = () => {
    const uniprot = this.props.uniprot;
    return <a href={`https://www.uniprot.org/uniprot/${uniprot}`} target="_blank">{uniprot}</a>
  }

  positionBody = (m: IMutation) => (
    <span className={classnames({
      "ll-mutation-table-selected-position": m.position === this.props.selectedPosition
    })}>
      {m.position}
    </span>
  )

  wtBody = (m: IMutation) => (
    this.getAminoAcidLabel(this.props.sequence[m.position-1], {fullName: true, style: 'strong-letter'})
  )

  mutBody = (m: IMutation) => (
    joinReactNodes(
      m.units.map(aminoAcid => this.getAminoAcidLabel(aminoAcid, {fullName: true, style: 'strong-letter'}))
    )
  )

  removeBtnBody = (m: IMutation) => (
    <RemoveButton
      title="Remove mutation"
      confirmMsg={`Remove mutation ${this.props.sequence[m.position-1]}${m.position}${m.units[0]}?`}
      data={m.position}
      onClick={this.props.clearPosition}
    />
  )

  renderProteinTable() {
    return (
      <DataTable
        value={this.props.mutations}
        dataKey="position"
        responsiveLayout="scroll"  // "scroll" to avoid stacking
        className={this.tableClassName}
        loading={false}
        emptyMessage={this.noRowsMsg}
      >
        <Column body={this.nameBody} header="Protein" />
        <Column body={this.uniprotBody} header="UniProtKB" />
        <Column body={this.positionBody} header="Position" />
        <Column body={this.wtBody} header="Wild-type" />
        <Column body={this.mutBody} header="Mutation" />
        <Column body={this.removeBtnBody} style={this.removeBtnStyle} />
      </DataTable>
    )
  }

  codonPositionsBody = (m: ICodonMutation) => (
    <span className={classnames({
      "ll-mutation-table-selected-position": typeof this.props.selectedPosition === 'number' && m.positions.includes(this.props.selectedPosition)
    })}>
            {m.positions[0]}–{m.positions[2]}
        </span>
  )

  codonWtBody = (m: ICodonMutation) => (
    <span title={m.wildType.dna.map((val, idx) => `${m.positions[idx]}: ${val}`).join(', ')}>
            {m.wildType.dna.join(' ')}
        </span>
  )

  codonMutBody = (m: ICodonMutation) => (
    <span title={m.positions.map((pos, idx) => `${pos}: ${m.wildType.dna[idx]} → ${m.mutation.dna[idx]}`).filter((_, idx) => m.wildType.dna[idx] !== m.mutation.dna[idx]).join(', ')}>
            <span className={classnames({"ll-mutation-table-mutated": m.wildType.dna[0] !== m.mutation.dna[0]})}>{m.mutation.dna[0]}</span>
      {" "}
      <span className={classnames({"ll-mutation-table-mutated": m.wildType.dna[1] !== m.mutation.dna[1]})}>{m.mutation.dna[1]}</span>
      {" "}
      <span className={classnames({"ll-mutation-table-mutated": m.wildType.dna[2] !== m.mutation.dna[2]})}>{m.mutation.dna[2]}</span>
        </span>
  )

  codonAABody = (m: ICodonMutation) => (
    <span title={`${this.getAminoAcidLabel(m.wildType.aminoAcid, {fullName: true})} → ${this.getAminoAcidLabel(m.mutation.aminoAcid, {fullName: true})}`}>
            {this.getAminoAcidLabel(m.wildType.aminoAcid)} → {this.getAminoAcidLabel(m.mutation.aminoAcid)}
        </span>
  )

  codonRemoveBtnBody = (m: ICodonMutation) => (
    <RemoveButton
      title="Remove mutations"
      confirmMsg={`Remove all mutations in a codon at positions ${m.positions[0]}–${m.positions[2]}?`}
      data={m.positions}
      onClick={this.props.clearPosition}
    />
  )

  renderDnaTable() {
    if (this.props.codonMutations === null) {
      console.warn("Codon mutation array is null");
      return null;
    }
    return (
      <DataTable value={this.props.codonMutations} dataKey="positions"
                 responsiveLayout="scroll"  // "scroll" to avoid stacking
                 className={this.tableClassName}
                 loading={false}
                 emptyMessage={this.noRowsMsg}
      >
        <Column body={this.nameBody} header="Name" />
        <Column body={this.codonPositionsBody} header="Positions" />
        <Column body={this.codonWtBody} header="Wild-type" />
        <Column body={this.codonMutBody} header="Mutation" />
        <Column body={this.codonAABody} header="Amino acid" />
        <Column body={this.codonRemoveBtnBody} style={this.removeBtnStyle} />
      </DataTable>
    )
  }

  render() {
    return this.props.level === MutationLevelEnum.DNA ? this.renderDnaTable() : this.renderProteinTable();
  }

}

export default MutationTable;
